import { type ReactNode } from 'react';
import { X } from '@/components/icons';
import clsx from 'clsx';

export interface ItemsTableColumn<R> {
  key: string;
  header: ReactNode;
  width?: string;
  cell: (row: R, index: number, update: (patch: Partial<R>) => void) => ReactNode;
  align?: 'left' | 'center' | 'right';
  /** mobileLayout="grid3" only: position in the phone grid, and whether the cell spans the full row. */
  mobileOrder?: number;
  mobileFull?: boolean;
  /** mobileLayout="grid3" only: a read-only figure, shown as a small tinted tile with its label inside. */
  mobileTile?: boolean;
}

interface Props<R> {
  title?: ReactNode;
  rows: R[];
  columns: ItemsTableColumn<R>[];
  onChange: (rows: R[]) => void;
  newRow: () => R;
  totalLabel?: string;
  totalValue?: ReactNode;
  /** Hide the add/remove controls (e.g. when the parent form is read-only). */
  readOnly?: boolean;
  /**
   * Phone card layout. 'rows' (default): one LABEL value row per column.
   * 'grid3': a 3-column grid with the label above each value — opt-in for
   * forms with many short fields.
   */
  mobileLayout?: 'rows' | 'grid3';
}

/**
 * Editable items table used inside MRN / PR / RFQ / PO / GRN modals.
 * Caller defines its columns via the `columns` prop; this component handles
 * add/remove + row mutation wiring.
 */
export function ItemsTable<R>({
  title,
  rows,
  columns,
  onChange,
  newRow,
  totalLabel,
  totalValue,
  readOnly = false,
  mobileLayout = 'rows',
}: Props<R>) {
  function update(idx: number, patch: Partial<R>) {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...patch } as R;
    onChange(next);
  }
  function remove(idx: number) {
    onChange(rows.filter((_, i) => i !== idx));
  }
  function add() {
    onChange([...rows, newRow()]);
  }

  return (
    <div className="mt-2 overflow-hidden rounded-card border border-[var(--border-color)] sm:mt-4">
      <div className="flex items-center justify-between border-b border-[var(--border-color)] bg-accent/[0.05] px-2.5 py-1.5 sm:px-3.5 sm:py-2.5">
        <span className="text-[12px] font-extrabold text-accent sm:text-sm">{title ?? 'Items'}</span>
        {!readOnly && (
          <button
            type="button"
            onClick={add}
            className="rounded-md bg-white/[0.04] px-2 py-0.5 text-[11px] font-bold text-[var(--text-secondary)] hover:bg-accent/10 hover:text-accent sm:px-2.5 sm:py-1 sm:text-xs"
          >
            ＋ Add Item
          </button>
        )}
      </div>

      {/* Mobile: stacked card per row so every field is visible without
          horizontal scroll. Convention: the first column is the "title"
          and shown once at the top; remaining columns render as a
          label:value grid below. */}
      <div className="sm:hidden">
        {rows.map((row, idx) => {
          const [firstCol, ...restCols] = columns;
          return (
            <div
              key={idx}
              className={
                mobileLayout === 'grid3'
                  ? 'm-2 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-2.5'
                  : 'border-b border-white/[0.04] p-2 last:border-b-0'
              }
            >
              <div className="mb-1 flex items-start justify-between gap-1.5">
                <div className="min-w-0 flex-1">
                  {firstCol.cell(row, idx, (patch) => update(idx, patch))}
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-danger/30 bg-danger/10 text-xs text-danger hover:bg-danger/20"
                    aria-label="Remove row"
                    title="Remove"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
              {mobileLayout === 'grid3' ? (
                // Read-only figures as tinted tiles (label inside); inputs share one height.
                <div className="grid grid-cols-3 gap-2 text-[12px] [&_input]:!h-9 [&_select]:!h-9">
                  {restCols.map((c) =>
                    c.mobileTile ? (
                      <div
                        key={String(c.key)}
                        className={clsx('flex min-w-0 flex-col items-center justify-center rounded-md bg-[var(--bg-soft)] px-1 py-1.5 text-center', c.mobileFull && 'col-span-3')}
                        style={{ order: c.mobileOrder }}
                      >
                        <div className="truncate text-[9px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{c.header}</div>
                        <div className="font-semibold leading-tight">{c.cell(row, idx, (patch) => update(idx, patch))}</div>
                      </div>
                    ) : (
                      <div key={String(c.key)} className={clsx('min-w-0', c.mobileFull && 'col-span-3')} style={{ order: c.mobileOrder }}>
                        <div className="mb-0.5 truncate text-[9px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{c.header}</div>
                        <div className="flex min-w-0 items-center [&>*]:w-full">
                          {c.cell(row, idx, (patch) => update(idx, patch))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-[70px_1fr] gap-x-2 gap-y-1 text-[11px]">
                  {restCols.map((c) => (
                    <div key={String(c.key)} className="contents">
                      <div className="flex min-w-0 items-center truncate text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                        {c.header}
                      </div>
                      <div className="flex min-w-0 items-center justify-end [&>*]:w-full">
                        {c.cell(row, idx, (patch) => update(idx, patch))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {totalValue !== undefined && (
          <div className="flex items-center justify-between bg-white/[0.02] px-2.5 py-1.5">
            <span className="text-[11px] font-bold text-[var(--text-secondary)]">
              {totalLabel ?? 'Total'}
            </span>
            <span className="text-[13px] font-extrabold text-accent">{totalValue}</span>
          </div>
        )}
      </div>

      {/* Desktop: compact table. On wide screens every column fits with
          no scroll (min-w-full spreads columns to fill the modal). On
          narrower modals the table falls back to its natural min-width
          (sum of per-column minWidths) and the wrapper scrolls
          horizontally so nothing is clipped. Inputs get tight
          text-[11px]/px-1 styling so numeric values render legibly. */}
      <div className="hidden overflow-x-auto sm:block" style={{ WebkitOverflowScrolling: 'touch' }}>
        <table
          className={clsx(
            'w-full min-w-max border-collapse',
            // Compact input styling inside the table cells only.
            '[&_input]:!min-w-0 [&_input]:!w-full [&_input]:!px-1.5 [&_input]:!py-1 [&_input]:!text-[11px]',
            '[&_select]:!min-w-0 [&_select]:!w-full [&_select]:!px-1 [&_select]:!py-1 [&_select]:!text-[11px]',
          )}
        >
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={String(c.key)}
                  className={clsx(
                    'border-b border-[var(--border-color)] bg-accent/[0.04] px-1.5 py-2 text-[10px] font-extrabold uppercase tracking-wide text-[var(--text-muted)]',
                    c.align === 'right' && 'text-right',
                    c.align === 'center' && 'text-center',
                  )}
                  style={{ width: c.width, minWidth: c.width ?? '52px' }}
                >
                  {c.header}
                </th>
              ))}
              <th className="w-8 border-b border-[var(--border-color)] bg-accent/[0.04]"></th>
            </tr>
          </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b border-white/[0.04] last:border-b-0">
              {columns.map((c) => {
                // Hover tooltip on each cell — surfaces the full text
                // value even when a narrow column visually clips it.
                // Grabs the value from common input/select fields on
                // the rendered element via title="", which the browser
                // shows natively on hover.
                const rendered = c.cell(row, idx, (patch) => update(idx, patch));
                // Try to derive a tooltip from the row field named by
                // the column key (works for string / number values).
                const rowVal = (row as Record<string, unknown>)[c.key];
                const tooltip = rowVal != null && rowVal !== '' ? String(rowVal) : undefined;
                return (
                  <td
                    key={String(c.key)}
                    title={tooltip}
                    className={clsx(
                      'px-1 py-1 overflow-hidden',
                      c.align === 'right' && 'text-right',
                      c.align === 'center' && 'text-center',
                    )}
                  >
                    {rendered}
                  </td>
                );
              })}
              <td className="text-center">
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="flex h-6 w-6 items-center justify-center rounded-md border border-danger/30 bg-danger/10 text-xs text-danger hover:bg-danger/20"
                    aria-label="Remove row"
                    title="Remove"
                  >
                    <X size={13} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        {totalValue !== undefined && (
          <tfoot>
            <tr className="bg-white/[0.02]">
              <td
                colSpan={columns.length}
                className="px-2.5 py-2 text-right text-xs font-bold text-[var(--text-secondary)]"
              >
                {totalLabel ?? 'Total'}
              </td>
              <td className="px-2 py-2 text-right text-sm font-extrabold text-accent">{totalValue}</td>
            </tr>
          </tfoot>
        )}
        </table>
      </div>
    </div>
  );
}
